import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import dayjs from 'dayjs';
import {Button, cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import ChekinProSettings from 'ChekinProSettings';
import {useChekinSDK} from 'context/ChekinSDK';
import {
  formatOneDate,
  FormTypes,
  getMinCheckInDate,
  isAfter,
  isEqual,
  OPTION_VALUES,
  SEARCH_RESERVATION_FORM_NAMES,
} from 'pages/SearchReservation/utils';
import {
  AUTH_INIT_MODELS,
  DATE_FORMAT,
  LocalStorage,
  PATTERNS,
  useStatus,
  mergeRefs,
  useTimeoutRef,
} from '@chekin/core';
import {RESERVATION_ID_KEY, STATUSES} from 'utils/constants';
import {PATHS} from 'routes/constants';
import {usePath} from 'hooks';
import {useVirtualKeyboard} from 'hooks/useVirtualKeyboard';
import {useHousing} from '../../useHousing';
import {useReservationNotFound} from '../../useReservationNotFound';
import {useLayoutMode} from 'hooks/useLayoutMode';
import PageUniversalLink from 'components/PageUniversalLink';
import Datepicker, {DatepickerHandle} from 'components/common/Datepicker';
import {API} from '@chekin/api';
import {StatusModalStates, useErrorModal} from '@chekin/shared';
import {InputController as Input} from '@guestapp/old-ui-kit';
import {createSession} from 'components/sessions/utils';
import {ReservationNotFoundModal} from 'components/ReservationNotFoundModal';
import {ViewMainInfo} from '../../components/ViewMainInfo';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';
import {
  ContentButton,
  CustomBackButton,
  Fields,
  FormWrapper,
  StyledBackIcon,
  StyledFormFieldWrapper,
  Wrapper,
} from './styled';

function CheckInDateView() {
  const {t} = useTranslation();
  const {modelName} = useChekinSDK();
  const navigate = useNavigate();
  const {state} = useLocation();
  const {getFullPath} = usePath();
  const {isLoading, setStatus} = useStatus();
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = useTimeoutRef();
  const {canCreate, isNotFoundModalOpen, openNotFoundModal, closeNotFoundModal} =
    useReservationNotFound();

  const payloadFormRef = useRef<
    Partial<
      Omit<FormTypes, SEARCH_RESERVATION_FORM_NAMES.check_in_date> & {
        [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: string;
      }
    >
  >();
  const {housing, isLoading: isLoadingHousing, housingCountry} = useHousing();
  const isHousingLoading = modelName === AUTH_INIT_MODELS.housing && isLoadingHousing;
  const layoutMode = useLayoutMode();
  const [modalRegisterGuestStatus, setModalRegisterGuestStatus] = useState(
    StatusModalStates.IDLE,
  );

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: {errors, isSubmitted},
    watch,
    setValue,
    getValues,
  } = useForm<FormTypes>();
  const checkInDate = watch(SEARCH_RESERVATION_FORM_NAMES.check_in_date);
  const leadGuestEmail = watch(SEARCH_RESERVATION_FORM_NAMES.lead_email);

  const {
    isOpen: isOpenReservationStatusModal,
    openModal: openReservationStatusModal,
    closeModal: closeReservationStatusModal,
  } = useModalControls();

  const {mutate} = useMutation({
    mutationFn: (formData: FormTypes) => {
      const checkInDate = dayjs(
        formData[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
      ).format(DATE_FORMAT);

      const payload = {
        [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: checkInDate,
        [SEARCH_RESERVATION_FORM_NAMES.lead_email]:
          formData[SEARCH_RESERVATION_FORM_NAMES.lead_email],
      };
      return API.reservation.findReservation(payload);
    },
    onSuccess: (data, formData) => {
      const reservationId = data?.results?.[0]?.id;
      const reservationSignUpFormLink = data?.results?.[0]?.signup_form_link;

      if (reservationId) {
        if (ChekinProSettings.isSDK) {
          setStatus(STATUSES.success);
          setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
          ChekinProSettings.events.onReservationFoundFromHousing({id: reservationId});
          return;
        }

        createSession({reservationId});

        LocalStorage.set(RESERVATION_ID_KEY, reservationId);
        window.location.href = reservationSignUpFormLink;
        setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
      } else {
        const checkInDate = dayjs(
          formData[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
        ).format(DATE_FORMAT);

        payloadFormRef.current = {
          [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: checkInDate,
          [SEARCH_RESERVATION_FORM_NAMES.lead_email]:
            formData[SEARCH_RESERVATION_FORM_NAMES.lead_email],
        };
        setModalRegisterGuestStatus(StatusModalStates.IDLE);
        closeReservationStatusModal();
        setStatus(STATUSES.idle);
        canCreate ? goToNewReservation() : openNotFoundModal();
      }
    },
    onError: error => {
      setStatus(STATUSES.error);
      closeReservationStatusModal();
      displayError(error);
    },
  });

  const onSubmit = (formData: FormTypes) => {
    setStatus(STATUSES.loading);
    openReservationStatusModal();
    setModalRegisterGuestStatus(StatusModalStates.LOADING);
    mutate(formData);
  };

  const validateCheckInDate = useCallback(
    (value?: Date) => {
      if (!value) return value;

      const minCheckInDate = getMinCheckInDate();
      const isAfterMinCheckInDate =
        isEqual(value, minCheckInDate) || isAfter(value, minCheckInDate);
      if (isAfterMinCheckInDate) return true;
      const formatMinCheckInDate = formatOneDate({
        date: minCheckInDate,
        housingCountry: housingCountry,
      });
      return t(`minimum_check_in_date_is`, {date: formatMinCheckInDate});
    },
    [housingCountry, t],
  );

  const goToNewReservation = () => {
    const payload = payloadFormRef.current;

    const checkInDate = payload?.[SEARCH_RESERVATION_FORM_NAMES.check_in_date];

    navigate(getFullPath(PATHS.searchReservation.newReservation), {
      replace: true,
      state: {
        startDate: checkInDate,
        email: payload?.[SEARCH_RESERVATION_FORM_NAMES.lead_email],
        housingId: housing?.id,
        country: housingCountry,
        from: OPTION_VALUES.check_in_date_and_email,
        mode: state?.mode,
      },
    });
  };

  useEffect(() => {
    if (
      !ChekinProSettings.isSDK &&
      modalRegisterGuestStatus === StatusModalStates.SUCCESS
    ) {
      timeoutRef.current = setTimeout(() => {
        closeReservationStatusModal();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [closeReservationStatusModal, modalRegisterGuestStatus, timeoutRef]);

  useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        void trigger();
      }
    },
    [isSubmitted, trigger],
  );

  const datepickerRef = useRef<DatepickerHandle>(null);
  const clearInputRef = useRef<(() => void) | null>(null);

  const virtualKeyboardConfig = useMemo(
    () => ({
      fields: {
        [SEARCH_RESERVATION_FORM_NAMES.lead_email]: {
          getValue: () => getValues(SEARCH_RESERVATION_FORM_NAMES.lead_email) || '',
          setValue: (value: string) => {
            setValue(SEARCH_RESERVATION_FORM_NAMES.lead_email, value);
          },
          onBlur: () => {
            clearInputRef.current?.();
          },
        },
        check_in_date_day: {
          getValue: () => datepickerRef.current?.getDayValue() || '',
          setValue: (value: string) => {
            datepickerRef.current?.setDayValue(value);
          },
          onBlur: () => {
            clearInputRef.current?.();
          },
        },
        check_in_date_year: {
          getValue: () => datepickerRef.current?.getYearValue() || '',
          setValue: (value: string) => {
            datepickerRef.current?.setYearValue(value);
          },
          onBlur: () => {
            clearInputRef.current?.();
          },
        },
      },
    }),
    [getValues, setValue],
  );

  const {handleFocus, handleBlur, clearInput, keyboard} =
    useVirtualKeyboard(virtualKeyboardConfig);

  useEffect(() => {
    clearInputRef.current = clearInput;
  }, [clearInput]);

  return (
    <>
      <PageUniversalLink className="check-in-date-view">
        <Wrapper className="kiosko:mt-[360px] kiosko:gap-10">
          <ViewMainInfo
            title={t('find_your_booking')}
            subTitle={t('enter_the_check_in_date_and_the_lead_guest_email')}
          />

          {!state?.mode && layoutMode === 'default' && (
            <CustomBackButton
              label={t('back')}
              customIcon={<StyledBackIcon name="backArrow" size={15} />}
              iconProps={{position: 'left'}}
              onClick={() =>
                navigate(getFullPath(PATHS.searchReservation.main), {
                  state: {mode: OPTION_VALUES.check_in_date_and_email},
                })
              }
              width="auto"
              className="summary-view__back-btn"
              link
            />
          )}

          <FormWrapper>
            <Fields className="guest-form-fields kiosko:w-full kiosko:gap-[38px]">
              <StyledFormFieldWrapper className="kiosko:w-full kiosko:mb-10">
                <Controller
                  name={SEARCH_RESERVATION_FORM_NAMES.check_in_date}
                  rules={{required: t('required'), validate: validateCheckInDate}}
                  control={control}
                  render={({field: {ref: controllerRef, ...field}}) => {
                    return (
                      <Datepicker
                        ref={mergeRefs(datepickerRef, controllerRef)}
                        error={
                          errors[SEARCH_RESERVATION_FORM_NAMES.check_in_date]?.message
                        }
                        label={t('check_in_date')}
                        onFocus={() => {
                          handleFocus(SEARCH_RESERVATION_FORM_NAMES.check_in_date);
                        }}
                        onFieldFocus={subfield => {
                          handleFocus(`check_in_date_${subfield}`);
                        }}
                        onFieldBlur={subfield => {
                          handleBlur(`check_in_date_${subfield}`);
                        }}
                        {...field}
                      />
                    );
                  }}
                />
              </StyledFormFieldWrapper>

              <StyledFormFieldWrapper className="kiosko:w-full">
                <Input
                  {...register(SEARCH_RESERVATION_FORM_NAMES.lead_email, {
                    required: t('required'),
                    pattern: {
                      value: PATTERNS.email,
                      message: t('invalid_email'),
                    },
                  })}
                  type="email"
                  label={t('lead_guest_email')}
                  control={control}
                  error={errors[SEARCH_RESERVATION_FORM_NAMES.lead_email]?.message}
                  inputMode="email"
                  autoCorrect="off"
                  spellCheck={false}
                  autoCapitalize="none"
                  onFocus={() => {
                    handleFocus(SEARCH_RESERVATION_FORM_NAMES.lead_email);
                  }}
                  onBlur={() => {
                    handleBlur(SEARCH_RESERVATION_FORM_NAMES.lead_email);
                  }}
                />
              </StyledFormFieldWrapper>
            </Fields>
          </FormWrapper>

          <ContentButton>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading || !checkInDate || !leadGuestEmail || isHousingLoading}
              className={cn(
                'submit-btn',
                'min-w-[205px]',
                'kiosko:mx-auto kiosko:mt-4 kiosko:block',
              )}
              variant="default"
            >
              {t('continue_t')}
            </Button>
          </ContentButton>
        </Wrapper>
        <SearchingBookingModal
          isOpen={isOpenReservationStatusModal}
          status={modalRegisterGuestStatus}
        />
        <ErrorModal />
        <ReservationNotFoundModal
          open={isNotFoundModalOpen}
          onClose={closeNotFoundModal}
        />
        {keyboard}
      </PageUniversalLink>
    </>
  );
}
export {CheckInDateView};
